import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useApiService } from "../services/apiServices";
import { useMsal } from "@azure/msal-react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SignatureCanvas from "react-signature-canvas";
import "../styles/signstyle.css";

const ManageSignaturesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rawHandleApiRequest = useApiService(); // not used directly
  const handleApiRequest = useCallback(rawHandleApiRequest, []); // memoized
  const { accounts } = useMsal();
  const queryParams = new URLSearchParams(location.search);
  const pdfUrl = queryParams.get("pdfUrl");
  const id = queryParams.get("id");
  const page = queryParams.get("page");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [signatures, setSignatures] = useState([]);
  const [refreshSignatures, setRefreshSignatures] = useState(0);
  const [signaturesLoading, setSignaturesLoading] = useState(false);
  const [deletingSignatureId, setDeletingSignatureId] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState("");
  const [drawModalOpen, setDrawModalOpen] = useState(false);
  const signatureInputRef = useRef(null);
  const sigCanvas = useRef(null);

  const fetchSignatures = useCallback(async () => {
    try {
      setSignaturesLoading(true);
      setSignatureError("");
      const response = await handleApiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/signature/all`,
        "GET"
      );
      if (response && Array.isArray(response)) {
        setSignatures(response);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error loading signatures:", error);
      setSignatureError("Failed to load signatures. Please try again.");
    } finally {
      setSignaturesLoading(false);
    }
  }, [handleApiRequest]);

  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures, refreshSignatures]);

  const deleteSignature = async (signatureId) => {
    try {
      setDeletingSignatureId(signatureId);
      const url = `${process.env.REACT_APP_API_BASE_URL}/signature?id=${signatureId}`;
      await handleApiRequest(url, "DELETE");
      setRefreshSignatures((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting signature:", error);
      setSignatureError("Failed to delete signature. Please try again.");
    } finally {
      setDeletingSignatureId(null);
    }
  };

  const handleSignatureUploadToList = async (e) => {
    const file = e.target.files[0];
    if (file) {
      e.target.value = null;
      const formData = new FormData();
      formData.append("fileAttachment", file);
      try {
        setUploadingSignature(true);
        setSignatureError("");
        await handleApiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/signature/create`,
          "POST",
          formData
        );
        setTimeout(() => {
          setRefreshSignatures((prev) => prev + 1);
        }, 500);
      } catch (error) {
        console.error("Upload failed:", error);
        setSignatureError("Failed to upload signature. Please try again.");
      } finally {
        setUploadingSignature(false);
      }
    }
  };

  const handleSaveDrawnSignature = async () => {
    if (sigCanvas.current.isEmpty()) {
      setSignatureError("Please draw a signature before saving.");
      return;
    }
    setUploadingSignature(true);
    try {
      setSignatureError("");
      const dataURL = sigCanvas.current.toDataURL();
      const blob = await fetch(dataURL).then((r) => r.blob());
      const file = new File([blob], "drawn-signature.png", { type: "image/png" });
      const formData = new FormData();
      formData.append("fileAttachment", file);
      await handleApiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/signature/create`,
        "POST",
        formData
      );
      setTimeout(() => {
        setRefreshSignatures((prev) => prev + 1);
      }, 500);
      setDrawModalOpen(false);
      sigCanvas.current.clear();
    } catch (error) {
      console.error("Error saving drawn signature:", error);
      setSignatureError("Failed to save drawn signature. Please try again.");
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleSignatureUploadClick = () => {
    signatureInputRef.current && signatureInputRef.current.click();
  };

  const handleDrawSignature = () => {
    setDrawModalOpen(true);
  };



  return (
    <div className="app">
      <Box display="flex" flexDirection="column" minHeight="100vh">
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            px: 5,
            pt: 2,
            pb: 1,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Typography
              sx={{
                fontFamily: "Segoe UI, Roboto, sans-serif",
                fontSize: "1rem",
                fontWeight: "bold",
                color: "#212529",
              }}
            >
              Manage Signatures
            </Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{display: "flex",alignItems: "center",gap: 2,p: 2,flexWrap: { xs: "nowrap", sm: "wrap" },}}>
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {uploadingSignature && (
            <CircularProgress size={24} sx={{ color: "#4caf50" }} />
          )}
        </Box>
         <Button
            variant="contained"
            onClick={handleSignatureUploadClick}
            disabled={uploadingSignature}
            sx={{
              backgroundColor: "#e8f5e9",
              color: "#4caf50",
              fontSize: "0.875rem",
              px: 4,
              py: 1, // padding top and bottom

              paddingY: "6px", 
              paddingX: "16px", 
              minHeight: "36px", 
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: "#c8e6c9" },
            }}
          >
            Add Signature
          </Button>
          <Button
                variant="contained"
                onClick={handleDrawSignature}
                disabled={uploadingSignature}
                sx={{
                  backgroundColor: "#e8f5e9",
                  color: "#4caf50",
                  fontSize: "0.875rem",
                  px: 2, 
                  py: 1, 
                  paddingY: "6px", 
                  paddingX: "16px", 
                  minHeight: "36px", 
                  lineHeight: 1.5,
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#c8e6c9" },
                }}
              >
            Draw Signature
          </Button>
        </Box>

        {/* Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          {signatureError && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {signatureError}
            </Typography>
          )}
          {signaturesLoading && signatures.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress sx={{ color: "#4caf50" }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                  xl: "repeat(5, 1fr)",
                },
                gap: 2,
                p: 1,
              }}
            >
              {signatures.length === 0 ? (
     
  <Typography
  sx={{
    color: "text.secondary",
    fontSize: { xs: "1rem", sm: "1.2rem" },
    textAlign: "center",
    px: 2,
    whiteSpace: {
      xs: "normal", // allow wrapping on small screens
      sm: "nowrap", // prevent wrapping on larger screens
    },
    overflow: "visible",     // ensure no clipping
    textOverflow: "unset",   // no ellipsis
    mx: "auto",               // center horizontally
    transform: {
      xs: "none",
      sm: "translateX(200px)", // shift on medium and up
    },
  }}
>
  No signatures found. Add your first signature using the button above.
</Typography>



  
              ) : (
                signatures.map((signature) => (
                  <Box
                    key={signature.id}
                    sx={{ position: "relative", width: "100%", height: 100 }}
                  >
                    <Paper
                      
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        p: 1,
                        transition: "box-shadow 0.3s ease",
                        "&:hover": {
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      {deletingSignatureId === signature.id ? (
                        <CircularProgress size={24} sx={{ color: "#4caf50" }} />
                      ) : (
                        <img
                          src={`${signature.signatureUrl}?t=${Date.now()}`}
                          alt={`Signature ${signature.id}`}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Paper>
                    <IconButton
                      size="small"
                      sx={{ position: "absolute", top: 0, right: 0, color: "red" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSignature(signature.id);
                      }}
                      disabled={deletingSignatureId === signature.id}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            ref={signatureInputRef}
            style={{ display: "none" }}
            onChange={handleSignatureUploadToList}
          />
        </Box>
      </Box>

      {/* Draw Signature Modal */}
      <Modal open={drawModalOpen} onClose={() => setDrawModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Draw Your Signature</Typography>
            <IconButton onClick={() => setDrawModalOpen(false)} sx={{ color: "red" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <SignatureCanvas
            ref={sigCanvas}
            penColor="#000"
            canvasProps={{
              width: isSmallScreen ? 300 : 400,
              height: 200,
              className: "sigCanvas",
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => sigCanvas.current.clear()}
              sx={{
                color: "#4caf50",
                borderColor: "#4caf50",
                "&:hover": { borderColor: "#388e3c" },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveDrawnSignature}
              disabled={uploadingSignature}
              sx={{
                backgroundColor: "#e8f5e9",
                color: "#4caf50",
                "&:hover": { backgroundColor: "#c8e6c9" },
              }}
            >
              {uploadingSignature ? (
                <CircularProgress size={24} sx={{ color: "#4caf50" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ManageSignaturesPage;
